<template>
  <div class="fts-design-workflow-drawer">
    <fts-drawer data-ref="lite-design-overlay" v-if="visible" position="right" width="400px" @close="close">
      <!-- header with tabs -->
      <template #drawerHeader>
        <div v-if="showDetailView" class="workflow-drawer-header">
          <div class="workflow-drawer-header-content" style="justify-content: flex-start;">

            <fts-btn icon text @click="closeDetailView">
              <font-awesome-icon :icon="['fas', 'chevron-right']" style="transform: rotate(180deg);" />
            </fts-btn>
            <h4 class="workflow-drawer-header-title">
              <template v-if="detailViewType === 'action'">{{ isEditingAction ? 'Edit action button' : 'New action button' }}</template>
              <template v-else-if="detailViewType === 'flow'">{{ isEditingFlow ? 'Edit flow' : 'New flow' }}</template>
            </h4>
          </div>
          <hr>
        </div>
        <div v-else class="workflow-drawer-header">
          <div class="workflow-drawer-header-content">
            <div class="workflow-drawer-tabs">
              <div v-for="tab in tabs" :key="tab.id" class="workflow-drawer-tab"
                :class="{ active: activeTab === tab.id }" @click="activeTab = tab.id">
                {{ tab.name }}
              </div>
            </div>
            <fts-btn icon text @click="close">
              <font-awesome-icon :icon="['fas', 'times']" />
            </fts-btn>
          </div>
          <hr>
        </div>
      </template>

      <!-- body with tab content -->
      <template #drawerContent>
        <!-- Detail view -->
        <div v-if="showDetailView" class="workflow-detail-view">
          <div class="workflow-detail-content">
            <!-- Flow form -->
            <div v-if="detailViewType === 'flow'">
              <!-- error -->
              <div v-if="error('fromStateId') || error('activityId')" class="workflow-form-error">
                {{ error('fromStateId') || error('activityId') }}
              </div>

              <!-- from state -->
              <fts-form-element :form-data="flowFormData" :item="{
                fieldName: 'fromStateId',
                displayName: 'From State',
                icon: 'fts-icon-menu-caret-down',
              }" :validator="$v.flowFormData" comfort-design disable-validator-error>
                <fts-select slot="formElement" slot-scope="{onInput, onBlur}" v-model="fromStateId" :options="states"
                  :clearable="Boolean(flowFormData.fromStateId)" comfort-design @input="onInput" @search:blur="onBlur">
                  <fts-state :tint="true" slot="selected-option-container" slot-scope="{option}" :state="option" />
                  <fts-state :tint="true" slot="option" slot-scope="{option}" :state="option" />
                </fts-select>
              </fts-form-element>

              <!-- activity -->
              <fts-form-element :form-data="flowFormData" :item="{
                fieldName: 'activityId',
                displayName: 'Activity',
                icon: 'fts-icon-menu-caret-down',
              }" :validator="$v.flowFormData" comfort-design disable-validator-error>
                <fts-select slot="formElement" slot-scope="{ formData, item, onInput, onBlur }" :form-data="formData"
                  :item="item" :options="actions" comfort-design @input="onInput" @search:blur="onBlur">
                  <fts-label-group slot="selected-option-container" slot-scope="{ option }" :item-id="option.id"
                    :list="actions" emoji />
                  <fts-label-group slot="option" slot-scope="{ option }" :item-id="option.id" :list="actions" emoji />
                </fts-select>
              </fts-form-element>

              <!-- to state -->
              <fts-form-element :form-data="flowFormData" :item="{
                'fieldName': 'toStateId',
                'displayName': 'To State',
                'icon': 'fts-icon-menu-caret-down',
              }" :validator="$v.flowFormData" comfort-design disable-validator-error>
                <fts-select slot="formElement" v-model="toStateId" :options="states"
                  :clearable="Boolean(flowFormData.toStateId)" comfort-design>
                  <fts-state :tint="true" slot="selected-option-container" slot-scope="{option}" :state="option" />
                  <fts-state :tint="true" slot="option" slot-scope="{option}" :state="option" />
                </fts-select>
              </fts-form-element>
            </div>

            <!-- Action form -->
            <div v-else-if="detailViewType === 'action'" class="workflow-form">
              <!-- Button label -->
              <div class="workflow-form-field" v-if="isDefaultAction">
                <label class="workflow-form-label">{{ actionFormData.name }}</label>
                <div class="workflow-form-hint">Designing the form for standard activities like create, edit or view.</div>
              </div>
              <div class="workflow-form-field" v-else>
                <label class="workflow-form-label">Button label</label>
                <input v-model="actionFormData.name" type="text" class="workflow-inline-input" placeholder=""
                  ref="actionNameInput" />
                <div class="workflow-form-error" v-if="error('name')">
                  {{error('name')}}
                </div>
                <div class="workflow-form-hint">Users can click this button to take action.</div>
              </div>

              <!-- ai annotation -->
              <fts-ai-annotation
                :actor="actionFormData.actor"
                :instruction="actionFormData.ai_instruction"
                :hint="actionFormData.ai_hint"
                :confidence="actionFormData.confidence_threshold"
              />

              <!-- Form submission toggle -->
              <div class="workflow-form-toggle">
                <label class="workflow-form-label">Allow user to submit form</label>
                <fts-toggle-switch v-model="hasFormSubmission" :size="20" />
              </div>
              <hr />

              <!-- Form Designer (when form submission is enabled) -->
              <fts-form-designer :modules="modules" :activity-id="actionFormData.id" :form="actionFormData.design"
                document @save="saveAction" v-if="actionFormData.design" />
            </div>
          </div>

          <!-- Form actions -->
          <!-- <div class="workflow-form-actions">
            <fts-btn outline @click="closeDetailView">
              <font-awesome-icon :icon="['fas', 'times']" />
              Close
            </fts-btn>
            <fts-btn color="primary" @click="saveFlow" v-if="detailViewType === 'flow'">
              <font-awesome-icon :icon="['fas', 'check']" />
              {{ isEditingFlow ? 'Update' : 'Save' }}
            </fts-btn>
            <fts-btn color="primary" @click="saveAction" v-if="detailViewType === 'action'">
              <font-awesome-icon :icon="['fas', 'check']" />
              {{ isEditingAction ? 'Update' : 'Save' }}
            </fts-btn>
          </div> -->
        </div>

        <!-- List view -->
        <div v-else class="workflow-drawer-body">
          <!-- Action tab -->
          <div v-if="activeTab === 'action'" class="workflow-drawer-tab-content">
            <div class="tab-header">
              <div class="tab-title">All actions</div>
              <fts-tippy 
                content="Activities appear as buttons for users to take actions." 
                append-trigger-class="tab-hint"
              >
                <span class="material-symbols-outlined material-symbols-outlined--filled tab-hint-icon">info</span>
              </fts-tippy>
            </div>

            <div class="workflow-item default-action">
              <font-awesome-icon :icon="['fas', 'lock']" class="workflow-item__handle" />
              <div class="workflow-item-content">
                <div class="workflow-item-name">{{ defaultAction.name }}</div>
                <div class="workflow-item__label">Default</div>
              </div>
              <div class="workflow-item__actions">
                <fts-btn icon text @click="editAction(defaultAction)">
                  <font-awesome-icon :icon="['fas', 'gear']" />
                </fts-btn>
              </div>
            </div>

            <template v-if="(!actions || actions.length === 0) && !stateFormData.id">
              <div class="workflow-empty-state">
                <div class="listing-empty-state__icon">
                  <img :src="emptyActionImage" alt="empty-action-image">
                </div>

                <div class="workflow-empty-state__title">No actions yet</div>

                <div class="workflow-empty-state__description">
                  Create action buttons to collaborate with others in the workspace.
                </div>

                <fts-btn 
                  color="primary" 
                  box-shadow-none 
                  text-transform-init 
                  @click="createAction"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" />
                  New action button
                </fts-btn>
              </div>
              <div class="workflow-dummy-block"></div>
            </template>

            <template v-else>
              <draggable v-model="workflowData.actions" v-bind="dragOptions" handle=".workflow-item__handle"
                @start="onDrag = true" @end="onActionDragEnd">
                <div v-for="action in actions" :key="action.id" class="workflow-item">
                  <!-- handle -->
                  <font-awesome-icon :icon="['fas', 'grip-vertical']" class="workflow-item__handle" />
                  <!-- content -->
                  <div class="workflow-item-content">
                    <div class="workflow-item-name">{{ action.name }}</div>
                  </div>
                  <!-- actions -->
                  <div class="workflow-item__actions">
                    <fts-btn icon text @click="editAction(action)">
                      <font-awesome-icon :icon="['fas', 'pen']" />
                    </fts-btn>
                    <fts-btn icon text @click="deleteAction(action)">
                      <font-awesome-icon :icon="['fas', 'trash']" />
                    </fts-btn>
                  </div>
                </div>
              </draggable>

              <fts-btn @click="createAction" text text-transform-init color="var(--inistate-text-secondary-color)"
                style="font-weight: var(--inistate-font-weight-light); justify-content: flex-start;">
                <font-awesome-icon :icon="['fas', 'plus']" />
                <p> New action button</p>
              </fts-btn>
            </template>
          </div>

            <!-- State tab -->
          <div v-else-if="activeTab === 'state'" class="workflow-drawer-tab-content">
            <div v-if="(!states || states.length === 0) && !stateFormData.id" class="workflow-empty-state">
              <div class="listing-empty-state__icon">
                <img :src="emptyStateImage" alt="empty-state-image">
              </div>
              
              <div class="workflow-empty-state__title">No state created yet</div>

              <div class="workflow-empty-state__description">
                Use states to monitor task progress.
              </div>

              <fts-btn 
                color="primary" 
                box-shadow-none 
                text-transform-init 
                @click="createState"
              >
                <font-awesome-icon :icon="['fas', 'plus']" />
                New state
              </fts-btn>
            </div>

            <div v-else>
              <div class="tab-header">
                <div class="tab-title">All states</div>
                <fts-tippy 
                  content="Activities taken by your team can automatically update states." 
                  append-trigger-class="tab-hint"
                >
                  <span class="material-symbols-outlined material-symbols-outlined--filled tab-hint-icon">info</span>
                </fts-tippy>
              </div>
              <draggable v-model="workflowData.states" v-bind="dragOptions" handle=".workflow-item__handle"
                @start="onDrag = true" @end="onStateDragEnd">
                <div v-for="state in states" :key="state.id" class="workflow-item" :class="{'editing': stateFormData.id === state.id, 'default-action': state.initial}">
                  <!-- Edit mode -->
                  <template v-if="stateFormData.id === state.id">
                    <div ref="editStateContainer">
                      <div class="workflow-item-content" style="gap: 0.5rem;">
                        <input v-model="stateFormData.name" type="text" class="workflow-inline-input" placeholder="State name"
                          ref="editStateNameInput"/>
                        <font-awesome-icon v-if="editAutofillLoading" :icon="['fas', 'spinner']" spin
                          style="color: var(--fts-text-secondary); font-size: 0.875rem;" />
                        <fts-color-picker v-model="stateFormData.color" :colors="stateColors" :toggle-size="24"
                          :option-size="24" />
                      </div>
                      <div class="workflow-form-error" v-if="error('name')">
                        {{error('name')}}
                      </div>
                      <!-- ai annotation -->
                      <fts-ai-annotation
                        style="margin-top: 0.5rem;"
                        :instruction="state.ai_instruction"
                        :hint="state.ai_hint"
                      />
                    </div>
                  </template>
                  <!-- Display mode -->
                  <template v-else>
                    <!-- handle -->
                    <font-awesome-icon :icon="['fas', 'grip-vertical']" class="workflow-item__handle" />
                    <!-- content -->
                    <div class="workflow-item-content">
                      <fts-state :state="{
                        id: state.id,
                        name: state.name || '(undefined)',
                        color: state.color,
                      }" />
                      <div class="workflow-item-content" v-if="state.initial">
                        <div class="workflow-item__label">Initial state</div>
                      </div>
                    </div>
                    <!-- actions -->
                    <div class="workflow-item__actions">
                      <fts-btn icon text @click="editState(state)">
                        <font-awesome-icon :icon="['fas', 'pen']" />
                      </fts-btn>
                      <fts-btn icon text @click="deleteState(state)">
                        <font-awesome-icon :icon="['fas', 'trash']" />
                      </fts-btn>
                    </div>
                  </template>
                </div>
              </draggable>
              <!-- Inline state creation form -->
              <div v-if="isNewState && stateFormData.id" class="workflow-item editing">
                <div ref="editStateContainer">
                  <div class="workflow-item-content" style="gap: 0.5rem;">
                    <input v-model="stateFormData.name" type="text" class="workflow-inline-input" placeholder="State name"
                      ref="editStateNameInput"/>
                    <font-awesome-icon v-if="editAutofillLoading" :icon="['fas', 'spinner']" spin
                      style="color: var(--fts-text-secondary); font-size: 0.875rem;" />
                    <fts-color-picker v-model="stateFormData.color" :colors="stateColors" :toggle-size="24" :option-size="24" />
                  </div>
                  <div class="workflow-form-error" v-if="error('name')">
                    {{error('name')}}
                  </div>
                </div>
              </div>

              <!-- Create state button -->
              <fts-btn v-else @click="createState" text text-transform-init color="var(--inistate-text-secondary-color)"
                style="font-weight: var(--inistate-font-weight-light); justify-content: flex-start;">
                <font-awesome-icon :icon="['fas', 'plus']" />
                <p> New state</p>
              </fts-btn>
            </div>

          </div>

          <!-- Flow tab -->
          <div v-else-if="activeTab === 'flow'" class="workflow-drawer-tab-content">
            <div v-if="!flows || flows.length === 0" class="workflow-empty-state">
              <div class="listing-empty-state__icon">
              <img :src="emptyFlowImage" alt="empty-flow-image">
              </div>

              <div class="workflow-empty-state__title">No flow created yet</div>

              <div class="workflow-empty-state__description">
                Use flows to update states automatically when action are taken.
              </div>

              <fts-btn 
                color="primary" 
                box-shadow-none 
                text-transform-init 
                @click="createFlow"
              >
                <font-awesome-icon :icon="['fas', 'plus']" />
                New flow
              </fts-btn>
            </div>

            <div v-else>
              <div class="tab-header">
                <div class="tab-title">All flows</div>
                <fts-tippy 
                  content="Set flows to update states automatically when actions are taken by users." 
                  append-trigger-class="tab-hint"
                >
                  <span class="material-symbols-outlined material-symbols-outlined--filled tab-hint-icon">info</span>
                </fts-tippy>
              </div>
              <draggable v-model="workflowData.flows" v-bind="dragOptions" handle=".workflow-item__handle"
                @start="onDrag = true" @end="onDrag = false">
                <div v-for="flow in flows" :key="flow.id" class="workflow-item workflow-flow-item">
                  <!-- handle -->
                  <font-awesome-icon :icon="['fas', 'grip-vertical']" class="workflow-item__handle" />
                  <!-- content -->
                  <div class="workflow-flow-path">
                    <!-- from state -->
                    <fts-state :tint="true" :state="{
                      id: flow.fromStateId,
                      name: getState(flow.fromStateId).name || '(any state)',
                      color: getState(flow.fromStateId).color,
                    }" class="flow-state" v-if="flow.fromStateId && getState(flow.fromStateId)" />
                     <fts-state
                      :state="{ name: '(any state)' }"
                      class="flow-state"
                      v-else
                    />
                    <!-- direction -->
                    <font-awesome-icon :icon="['fas', 'right-long']" class="flow-direction" />
                    <!-- activity -->
                    <div class="flow-activity" v-if="getAction(flow.activityId)">
                      <fts-moji :size="14" :emoji="getAction(flow.activityId).emoji" class="flow-activity-emoji"
                        v-if="getAction(flow.activityId).emoji" />
                      <div class="flow-activity-name" :style="{
                        color: getAction(flow.activityId).color,
                      }">
                        {{ getAction(flow.activityId).name }}
                      </div>
                    </div>
                    <div class="flow-no-activity" v-else>(no action)</div>
                    <!-- direction -->
                    <font-awesome-icon :icon="['fas', 'right-long']" class="flow-direction" />
                    <!-- to state -->
                    <template v-if="
                      flow.conditional &&
                      flow.conditionalStates &&
                      flow.conditionalStates.length
                    ">
                      <template v-for="(state, stateIndex) in flow.conditionalStates">
                        <div :key="`${state.id}-or`" class="flow-conditional" v-if="stateIndex">
                          or
                        </div>
                        <fts-state :tint="true" :key="state.id" :state="{
                          name: getState(state.toStateId).name,
                          color: getState(state.toStateId).color,
                        }" class="flow-state" v-if="getState(state.toStateId)" />
                      </template>
                    </template>
                    <fts-state :tint="true" :state="{
                      id: flow.fromStateId,
                      name: getState(flow.toStateId).name || '(any state)',
                      color: getState(flow.toStateId).color,
                    }" class="flow-state" v-else-if="flow.toStateId && getState(flow.toStateId)" />
                    <fts-state :tint="true" :state="{ name: '(any state)' }" class="flow-state" v-else />
                  </div>
                  <div class="workflow-item__actions">
                    <fts-btn icon text @click="editFlow(flow)">
                      <font-awesome-icon :icon="['fas', 'pen']" />
                    </fts-btn>
                    <fts-btn icon text @click="deleteFlow(flow)">
                      <font-awesome-icon :icon="['fas', 'trash']" />
                    </fts-btn>
                  </div>
                </div>
              </draggable>
              <fts-btn @click="createFlow" text text-transform-init color="var(--inistate-text-secondary-color)"
                style="font-weight: var(--inistate-font-weight-light); justify-content: flex-start;">
                <font-awesome-icon :icon="['fas', 'plus']" />
                <p> New flow</p>
              </fts-btn>
            </div>
          </div>
        </div>
      </template>

      <template #drawerLowerContent v-if="showDetailView && (isEditingAction || isEditingFlow) && !isDefaultAction">
        <div class="info-drawer-actions">
          <fts-btn text text-transform-init @click="remove">
            <font-awesome-icon :icon="['far', 'trash-alt']" /> Delete
          </fts-btn>
          <div class="info-drawer-actions-spacer"></div>
        </div>
      </template>
    </fts-drawer>

  </div>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import draggable from "vuedraggable";
import changesets from 'diff-json';
import { authorizationStore } from "../store/authorizationStore";
import validator from "../commons/designValidator";
import commons from "../commons/commons";
import FTSDrawer from "../elements/FTSDrawer";
import FTSBtn from "../elements/FTSBtn";
import FTSState from "../elements/FTSState";
import FTSMoji from "../elements/FTSMoji";
import FTSTippy from "../elements/FTSTippy";
import FTSDropdown from "../elements/FTSDropdown";
import FTSColorPicker from "../elements/FTSColorPicker";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSFormElement from "../elements/FTSFormElement";
import FTSSelect from "../elements/FTSSelect";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSFormDesigner from "../elements/FTSFormDesigner";
import FTSAiAnnotation from "./FTSAiAnnotation";
import { alphaPalette } from "../commons/colors";
import { store } from "../store/canvasDesignStore";
import canvasDesign from "../commons/canvasDesign";
import { clientStore } from '../store/clientStore';
import { emptyActionImage, emptyFlowImage, emptyStateImage } from "../commons/images";
import { offerBlockRewrites } from "./automation/blockRewrites";

export default {
  store,
  components: {
    draggable,
    "fts-drawer": FTSDrawer,
    "fts-btn": FTSBtn,
    "fts-state": FTSState,
    "fts-moji": FTSMoji,
    "fts-tippy": FTSTippy,
    "fts-dropdown": FTSDropdown,
    "fts-color-picker": FTSColorPicker,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-form-element": FTSFormElement,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-form-designer": FTSFormDesigner,
    "fts-ai-annotation": FTSAiAnnotation,
  },
  props: {
    workflowData: {
      type: Object,
      default: () => ({
        standardActions: [],
        actions: [],
        states: [],
        flows: []
      })
    },
  },
  data: () => ({
    visible: false,
    activeTab: 'action',
    tabs: [
      { id: 'action', name: 'Action' },
      { id: 'state', name: 'State' },
      { id: 'flow', name: 'Flow' }
    ],
    onDrag: false,
    dragOptions: {
      animation: 200,
      disabled: false,
      ghostClass: 'ghost'
    },
    showDetailView: false,
    detailViewType: null,
    flowFormData: {
      fromStateId: null,
      activityId: null,
      toStateId: null
    },
    actionFormData: {
      id: null,
      name: '',
      design: null,
    },
    stateFormData: {
      id: null,
      name: '',
      color:'#3B82F6',
    },
    editingActionId: null,
    isEditingAction: false,
    editingFlowId: null,
    isEditingFlow: false,
    stateColors: alphaPalette,
    editAutofillLoading: false,
    originalFormData: null,
    insertAt: null,
    emptyActionImage,
    emptyFlowImage,
    emptyStateImage,
  }),
  computed: {
    isDefaultAction(){
      return this.actionFormData.id === 'create';
    },
    defaultAction(){
      return this.workflowData.standardActions.find((x)=> x.id === 'create');
    },
    actions() {
      return this.workflowData.actions || [];
    },
    states() {
      return this.workflowData.states.filter(x => x.name);
    },
    flows() {
      return this.workflowData.flows || [];
    },
    fromStateId: {
      get() {
        return Boolean(this.flowFormData && this.flowFormData.fromStateId) ? this.flowFormData.fromStateId : "";
      },
      set(val) {
        this.$set(this.flowFormData, "fromStateId", Boolean(val) ? val : null);
      }
    },
    toStateId: {
      get() {
        return Boolean(this.flowFormData && this.flowFormData.toStateId) ? this.flowFormData.toStateId : "";
      },
      set(val) {
        this.$set(this.flowFormData, "toStateId", Boolean(val) ? val : null);
      }
    },
    activityId: {
      get() {
        return Boolean(this.flowFormData && this.flowFormData.activityId) ? this.flowFormData.activityId : "";
      },
      set(val) {
        this.$set(this.flowFormData, "activityId", Boolean(val) ? val : null);
      }
    },
    modules() {
      return this.$store.getters.workspaceModules;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    hasFormSubmission: {
      get() {
        return Boolean(this.actionFormData.design);
      },
      set(val) {
        if (val) {
          this.$set(this.actionFormData, "design", {
            rows: [{ index: 0, items: [] }],
            backendScript: "",
            payload: "{}"
          });
        } else {
          this.$set(this.actionFormData, "design", null);
        }
      }
    },
    isNewState() {
      return !this.states.some(a => a.id === this.stateFormData.id);
    },
  },
  validations() {
    const flowFormData = validator.flowDesign(this.flows, this.flowFormData);
    const actionFormData = validator.actionDesign(this.actions);
    const stateFormData = validator.stateDesign(this.states);
    return { flowFormData, actionFormData, stateFormData };
  },
  created() {
  },
  methods: { 
    show(tab = 'action') {
      console.log(this.workflowData)
      this.activeTab = tab;
      this.visible = true;
    },
    async remove() {
      /* this should only trigger in detail form */
      if(!this.showDetailView) return;

      let success = false;
      if(this.actionFormData.id){
        success = await this.deleteAction(this.actionFormData);
      } else if(this.flowFormData.id){
        success = await this.deleteFlow(this.flowFormData);
      }

      if(success) this.backToList();
    },
    async close() {
      let dismiss = true;
      if(this.showDetailView) dismiss = await this.closeDetailView();
      /* blocking the entire side drawer to close */
      if(!dismiss) return;

      this.insertAt = null;
      this.visible = false;
      window.eventBus.$emit("listing:schemaUpdated");
      window.eventBus.$emit("list-view-form:schemaUpdated");
    },
    error(key) {
      if(this.actionFormData.id){
        /* return error action */
        return validator.actionDesignError(this.$v.actionFormData, key);
      }else if(this.flowFormData.id){
        /* return error flow */
        return validator.flowDesignError(this.$v.flowFormData, key);
      }else if(this.stateFormData.id){
        return validator.stateDesignError(this.$v.stateFormData, key);
      }

      return;
    },
    async closeDetailView() {
       
      return new Promise(async (resolve) => {
        
        if(this.actionFormData.id){

          if(this.isDefaultAction){
            await this.saveAction();
          }

          /* check form validation */
          this.$v.actionFormData.$touch();
          if (!this.$v.actionFormData.$invalid) {
            await this.saveAction();
            this.resetActionForm();
            this.backToList();
            resolve(true);
            return;
          } else if(!this.$v.actionFormData.name.required && !this.isEditingAction){
            this.resetActionForm();
            this.backToList();
            resolve(true);
            return;
          }
          this.$modal.show("dialog", {
            title: "Confirm discard changes",
            text: "Discard all changes?",
            buttons: [
              { title: "Cancel", handler: () => {
                this.$modal.hide("dialog"); 
                resolve(false);
              } },
              {
                title: "Discard",
                default: true,
                handler: () => {
                  this.$modal.hide("dialog");
                  this.resetActionForm();
                  this.backToList();
                  resolve(true);
                },
              },
            ],
          });
          console.warn("WorkflowDrawer:action:InvalidForm", validator.translateValidations(this.$v.actionFormData, true));
          return;
        }else if(this.flowFormData.id){
          this.$v.flowFormData.$touch();
          if (!this.$v.flowFormData.$invalid) {
            await this.saveFlow();
            this.resetFlowForm();
            this.backToList();
            resolve(true);
            return;
          } else if ( !this.$v.flowFormData.fromStateId.required && !this.$v.flowFormData.activityId.required && !this.isEditingFlow){
            this.resetFlowForm();
            this.backToList();
            resolve(true);
            return;
          }
          this.$modal.show("dialog", {
            title: "Confirm discard changes",
            text: "Discard all changes?",
            buttons: [
              { title: "Cancel", handler: () => {
                this.$modal.hide("dialog"); 
                resolve(false);
              } },
              {
                title: "Discard",
                default: true,
                handler: () => {
                  this.$modal.hide("dialog");
                  this.resetFlowForm();
                  this.backToList();
                  resolve(true);
                },
              },
            ],
          });
 
          console.warn("WorkflowDrawer:flow:InvalidForm", validator.translateValidations(this.$v.flowFormData, true));
          return;
        } 

      });

    },
    resetFlowForm() {
      this.$v.flowFormData.$reset();
      this.flowFormData = {
        id: null,
        fromStateId: null,
        activityId: null,
        toStateId: null
      };

      // Reset editing flags
      this.editingFlowId = null;
      this.isEditingFlow = false;
      this.originalFormData = null;

      // Reset validation
      if (this.$v && this.$v.flowFormData) {
        this.$v.flowFormData.$reset();
      }
    },
    resetActionForm() {
      this.$v.actionFormData.$reset();
      this.actionFormData = {
        id: null,
        name: '',
        design: null,
      };
      this.editingActionId = null;
      this.isEditingAction = false;
      this.originalFormData = null;
    },
    createAction() {
      this.resetActionForm();
      // Generate new ID for the action
      this.actionFormData.id = commons.newUUID();
      this.showDetailView = true;
      this.detailViewType = 'action';
      this.$nextTick(() => {
        if (this.$refs.actionNameInput) {
          this.$refs.actionNameInput.focus();
        }
      });
    },
    editAction(action) {
      this.actionFormData = {
        id: action.id,
        name: action.name || '',
        design: action.design ? _.cloneDeep(action.design) : null,
        // carried for display only; saveAction preserves these via canvas spread
        ai_instruction: action.ai_instruction,
        ai_hint: action.ai_hint,
        actor: action.actor,
        confidence_threshold: action.confidence_threshold,
      };

      // Expand form design with information data (displayName, type, etc.)
      if (this.actionFormData.design) {
        commons.expandWorkDesign(this.information, this.actionFormData.design);
      }

      this.editingActionId = action.id;
      this.isEditingAction = true;
      this.showDetailView = true;
      this.detailViewType = 'action';

      this.originalFormData = _.cloneDeep(this.actionFormData);
      this.$nextTick(() => {
        if (this.$refs.actionNameInput) {
          this.$refs.actionNameInput.focus();
        }
      });
    },
    async deleteAction(action) {
      // Check if action is used in any flow
      if (this.isActionUsedInFlows(action.id)) {
        this.$toasted.error("Cannot delete this action. It is used in one or more flows.");
        return;
      }

      const res = await this.$confirmation.show({
        isLite: true,
        type: "delete",
        title: "Delete action button?",
        text: "This action cannot be undone."
      });

      if(res !== true) return;

      let canvas = _.cloneDeep(this.$store.state.canvas);
      const idx = canvas.activities.findIndex(a => a.id === action.id);
      if (idx !== -1) {
        canvas.activities.splice(idx, 1);
      }
      this.$store.dispatch("updateVectorCanvas", canvas);

      await this.$store.dispatch("saveVector");

      this.$alertSuccess("Action deleted.");

      return true;
    },
    async onActionDragEnd() {
      this.onDrag = false;
      let canvas = _.cloneDeep(this.$store.state.canvas);
      canvas.activities = [...this.workflowData.actions];
      this.$store.dispatch("updateVectorCanvas", canvas);

      await this.$store.dispatch("saveVector");
    },
    createState() {
      this.stateFormData = canvasDesign.initState({
        id: null,
        name: '',
        color:'#3B82F6',
        initial: this.states.length === 0
      });
      this.$nextTick(() => {
        const input = this.$refs.editStateNameInput;
        if (input) {
          (Array.isArray(input) ? input[0] : input).focus();
        }
      });
    },
    editState(state) {
      this.stateFormData = {
        id: state.id,
        name: state.name,
        color: state.color,
        initial: state.initial,
      };
      this.originalFormData = _.cloneDeep(this.stateFormData);

      this.$nextTick(() => {
        const input = this.$refs.editStateNameInput;
        if (input) {
          (Array.isArray(input) ? input[0] : input).focus();
        }
      });
    },
    handleStateClickOutside(e) {
      if (!this.stateFormData.id) return;
      const ref = this.$refs.editStateContainer;
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (!el) return;
      // debugger
      if (el.contains(e.target)) return;
      this.saveState();
    },

    async saveState() {
      if(this.originalFormData){
        const diff = changesets.diff(this.stateFormData, this.originalFormData);
          if (!diff || !diff.length) {
            this.resetStateForm();
            return;
        }
      }

      this.$v.stateFormData.$touch();

      if(this.$v.stateFormData.$anyError || !this.stateFormData.id) return;

      let canvas = _.cloneDeep(this.$store.state.canvas);

      if(!canvas.states){
        canvas.states = [];
      }

      const stateIndex = canvas.states.findIndex(s => s.id === this.stateFormData.id);

      if (stateIndex !== -1) {
        canvas.states[stateIndex] = {
          ...this.stateFormData
        };
      } else {
        canvas.states.push(
          {
            ...this.stateFormData
          }
        );
      }

      const remainingStates = canvas.states.filter(s => s.name);
      if (remainingStates.length > 0) {
        this.addStateColumnToListings(canvas);
      }

      this.$store.dispatch("updateVectorCanvas", canvas);

      this.$store.dispatch("saveVector").then(res => {
        clientStore.dispatch("setWorkspaceModuleStates", {
          moduleId: res.data.id,
          states: canvas.states
        });
      }).catch(err => {
        // SS06419: a refused save used to vanish while its state chip stayed on screen.
        this.$toasted.global.error({ response: err && err.response, message: "Failed to save the state" });
      });

      this.resetStateForm();
    },
    resetStateForm() {
      this.editAutofillLoading = false;
      this.stateFormData = {
        id: null,
        name: '',
        color:'#3B82F6',
      };
      this.originalFormData = null;
      this.$v.stateFormData.$reset();
    },
    async deleteState(state) {
      // Check if state is used in any flow
      if (this.isStateUsedInFlows(state.id)) {
        this.$toasted.error("Cannot delete this state. It is used in one or more flows.");
        return;
      }

      const res = await this.$confirmation.show({
        isLite: true,
        type: "delete",
        title: "Delete state?",
        text: "This action cannot be undone."
      });

      if(res !== true) return;

      // Clone canvas from store
      let canvas = _.cloneDeep(this.$store.state.canvas);

      // Find and remove the state
      const stateIndex = canvas.states.findIndex(s => s.id === state.id);
      if (stateIndex !== -1) {
        canvas.states.splice(stateIndex, 1);
      }

      // Ensure first state is initial after deletion
      this.ensureFirstStateIsInitial(canvas);

      // If no states remaining, remove state column from all listings
      const remainingStates = canvas.states.filter(s => s.name);
      if (remainingStates.length === 0) {
        this.removeStateColumnFromListings(canvas);
      }

      // Dispatch to store to persist changes
      this.$store.dispatch("updateVectorCanvas", canvas);

      // Trigger save operation
      const response = await this.$store.dispatch("saveVector");

      clientStore.dispatch("setWorkspaceModuleStates", {
        moduleId: response.data.id,
        states: canvas.states
      });

      this.$alertSuccess("State deleted.");
    },
    async onStateDragEnd() {
      // Set drag flag to false
      this.onDrag = false;

      // Clone canvas from store
      let canvas = _.cloneDeep(this.$store.state.canvas);

      // Update the states with the reordered array
      canvas.states = [...this.workflowData.states];

      // Ensure first state is initial after reorder
      this.ensureFirstStateIsInitial(canvas);

      // Dispatch to store to persist changes
      this.$store.dispatch("updateVectorCanvas", canvas);

      // Trigger save operation
      await this.$store.dispatch("saveVector");
    },
    createFlow() {
      this.resetFlowForm();
      // Generate new ID for the action
      this.flowFormData.id = commons.newUUID();
      this.showDetailView = true;
      this.detailViewType = 'flow';
    },
    editFlow(flow) {
      // Load flow data into form
      this.flowFormData = {
        id: flow.id || null,
        fromStateId: flow.fromStateId || null,
        activityId: flow.activityId || null,
        toStateId: flow.toStateId || null
      };

      // Set editing flags
      this.editingFlowId = flow.id;
      this.isEditingFlow = true;

      // Show detail view
      this.showDetailView = true;
      this.detailViewType = 'flow';

      this.originalFormData = _.cloneDeep(this.flowFormData);
    },
    async deleteFlow(flow) {
      const res = await this.$confirmation.show({
        isLite: true,
        type: "delete",
        title: "Delete flow?",
        text: "This action cannot be undone."
      });

      if(res !== true) return;

      // Clone canvas from store
      let canvas = _.cloneDeep(this.$store.state.canvas);

      // Find and remove the flow
      const flowIndex = canvas.flows.findIndex(f => f.id === flow.id);
      if (flowIndex !== -1) {
        canvas.flows.splice(flowIndex, 1);
      }

      // Update store
      this.$store.dispatch("updateVectorCanvas", canvas);

      // Save to backend
      await this.$store.dispatch("saveVector");
      this.$alertSuccess("Flow deleted.");
      return true;
    },
    async saveFlow() {

      if(this.originalFormData){
        const diff = changesets.diff(this.flowFormData, this.originalFormData);
          if (!diff || !diff.length) {
          return;
        }
      }  

      let canvas = _.cloneDeep(this.$store.state.canvas);

      if (!canvas.flows) {
        canvas.flows = [];
      }

      if (this.isEditingFlow && this.editingFlowId) {
        // UPDATE existing flow
        const flowIndex = canvas.flows.findIndex(f => f.id === this.editingFlowId);
        if (flowIndex !== -1) {
          canvas.flows[flowIndex] = {
            ...canvas.flows[flowIndex],
            fromStateId: this.flowFormData.fromStateId || null,
            activityId: this.flowFormData.activityId || null,
            toStateId: this.flowFormData.toStateId || null
          };
        }
      } else {
        // CREATE new flow
        const newFlow = {
          id: commons.newUUID(),
          fromStateId: this.flowFormData.fromStateId || null,
          activityId: this.flowFormData.activityId || null,
          toStateId: this.flowFormData.toStateId || null,
          conditional: false,
          conditionalStates: []
        };
        canvas.flows.push(newFlow);
      }

      // Update store
      this.$store.dispatch("updateVectorCanvas", canvas);

      // Save to backend
      await this.$store.dispatch("saveVector");

      console.debug('WorkflowDrawer:flow:Saving view changes and close detail view.');
    },
    // Master plan (2026-08-01, refined same day): the save gate keys on the DESIGN's
    // serverNative flag, not the user — a server-native design must stay native-servable,
    // so normal users cannot save Python-lane constructs into it. Once an internal /
    // consultant user relaxes a design to the Python lane (flag off), it is freely
    // configurable by everyone. Delta-based within the gated path so a flagged design with
    // pre-existing Python constructs stays editable: only ADDING/CHANGING a script or
    // formula is gated. Classify failures fail-open — a network error must never block
    // design work.
    canRelaxServerNative() {
      const roles = authorizationStore.getters.userRoles || [];
      return authorizationStore.getters.isInternal || roles.indexOf("Consultant") > -1;
    },
    // every formula string in a design, with the enclosing table's display name for column
    // formulas (classification needs the per-row context), and the object and key holding it
    // (so the save gate can put the standard spelling in its place)
    collectDesignFormulas(design, parentName = null, found = []) {
      if (!design) return found;
      if (Array.isArray(design)) {
        design.forEach((item) => this.collectDesignFormulas(item, parentName, found));
        return found;
      }
      if (typeof design !== "object") return found;

      ["formula", "visibleFormula", "readOnlyFormula", "requiredFormula", "validateFormula"]
        .forEach((key) => {
          if (design[key] && String(design[key]).trim()) {
            found.push({ formula: String(design[key]), parentName, holder: design, key });
          }
        });

      Object.keys(design).forEach((key) => {
        if (key === "fields" && Array.isArray(design.fields)) {
          const tableName = design.displayName || design.name || parentName;
          this.collectDesignFormulas(design.fields, tableName, found);
        } else if (design[key] && typeof design[key] === "object") {
          this.collectDesignFormulas(design[key], parentName, found);
        }
      });

      return found;
    },
    // The converter's standard spelling of a formula the gate would refuse (gap-closure
    // §11.4a), offered to everyone before the refusal (or, for internal / consultant users,
    // before the save-anyway prompt). A checked rewrite says why it may answer differently.
    async offerStandardRewrite(short, rewrite) {
      const checks = rewrite.kind === "checked" && rewrite.checks && rewrite.checks.length
        ? ` Check it gives the same answers: ${rewrite.checks.join(" ")}` : "";
      const res = await this.$confirmation.show({
        title: "Use the standard syntax?",
        text: `A formula needs the Python engine (${short}). In the standard syntax, which runs natively, it reads ${rewrite.formula}.${checks} Replace it and save?`,
      });
      return res === true;
    },
    // A Python-lane violation on a server-native design (master plan confirmation
    // 2026-08-01 #2): normal users are blocked outright; internal/consultant users are
    // prompted and may choose to proceed (the design stays flagged; the affected parts run
    // on the Python engine via the runtime's own fallback).
    async confirmPythonLaneViolation(reason) {
      if (!this.canRelaxServerNative()) {
        this.$toasted.error(
          `Can't save: ${reason} Ask a consultant to switch this design off server-native if the Python engine is truly needed.`);
        return false;
      }
      const res = await this.$confirmation.show({
        title: "Needs the Python engine",
        text: `${reason} The design stays server-native and the affected parts will run on the Python engine. Save anyway?`,
      });
      return res === true;
    },
    async assertServerNativeDesign() {
      const design = this.actionFormData && this.actionFormData.design;
      if (!design) return true;
      const original = (this.originalFormData && this.originalFormData.design) || null;

      // the ACTIVITY's flag gates formulas and scripts; the automation container's own
      // flag gates the graph (each is what the runtimes read)
      const activityServerNative = Boolean(this.actionFormData)
        && this.actionFormData.serverNative === true;

      if (activityServerNative) {
        // scripts are Python-only: adding or changing one on a server-native form
        for (const scriptKey of ["backendScript", "beforeDoneScript", "afterDoneScript"]) {
          const next = String(design[scriptKey] || "").trim();
          const prev = String((original && original[scriptKey]) || "").trim();
          if (next && next !== prev) {
            if (!(await this.confirmPythonLaneViolation(
              "scripts run on the Python engine and this form is server-native.")))
              return false;
            break; // one confirmation covers the script change
          }
        }
      }

      // a server-native form automation graph must classify server-native
      if (design.automation && design.automation.serverNative === true
        && design.automation.blocks
        && Object.keys(design.automation.blocks).length) {
        const snapshot = _.cloneDeep(design.automation.blocks);
        Object.values(snapshot).forEach((node) => {
          if (node.name != "start" && node.data) {
            delete node.data.variables;
            delete node.data.priorVariables;
            if (node.data.actions) {
              node.data.actions.forEach((action) => {
                delete action.variables;
                delete action.priorVariables;
              });
            }
          }
        });
        try {
          const res = await this.$store.dispatch("classifyAutomationNative", {
            vectorId: this.$store.state.vectorId,
            activityId: this.editingActionId || null,
            isForm: true,
            automationBlocks: { blocks: snapshot },
          });
          const verdict = res && res.data;
          // the standard spellings that take the graph native, offered before the refusal
          if (verdict && verdict.compatible === false
            && !(await offerBlockRewrites(this, verdict, design.automation.blocks))) {
            const blockers = (verdict.findings || [])
              .map((f) => f.reason).filter(Boolean).join(" • ") || verdict.reason || "unsupported construct";
            if (!(await this.confirmPythonLaneViolation(
              `this form's automation would need the Python engine (${blockers}).`)))
              return false;
          }
        } catch (err) {
          /* advisory endpoint unavailable — fail-open */
        }
      }

      if (!activityServerNative) return true;

      // changed or new formulas on a server-native form must classify client/server native
      const originalKeys = new Set(this.collectDesignFormulas(original)
        .map((f) => `${f.parentName || ""}|${f.formula}`));
      const changed = this.collectDesignFormulas(design)
        .filter((f) => !originalKeys.has(`${f.parentName || ""}|${f.formula}`));

      for (const item of changed) {
        try {
          const res = await this.$store.dispatch("classifyFormulaEngine", {
            activityId: this.editingActionId || null,
            formula: item.formula,
            parentName: item.parentName || null,
            propertyName: item.key,
          });
          const verdict = res && res.data;
          if (verdict && verdict.engine === "python") {
            const short = item.formula.length > 60
              ? `${item.formula.slice(0, 60)}…` : item.formula;
            const rewrite = verdict.rewrite && verdict.rewrite.formula ? verdict.rewrite : null;
            if (rewrite && (await this.offerStandardRewrite(short, rewrite))) {
              this.$set(item.holder, item.key, rewrite.formula);
              continue;
            }
            if (!(await this.confirmPythonLaneViolation(
              `a formula needs the Python engine (${short}).`)))
              return false;
          }
        } catch (err) {
          /* advisory endpoint unavailable — fail-open */
        }
      }

      return true;
    },
    async saveAction() {

      if(this.originalFormData){
        const diff = changesets.diff(this.actionFormData, this.originalFormData);
          if (!diff || !diff.length) {
          return;
        }
      }

      if (!(await this.assertServerNativeDesign())) return;

      let canvas = _.cloneDeep(this.$store.state.canvas);

      if(this.isDefaultAction){
        const idx = canvas.standardActivities.findIndex(a => a.id === this.editingActionId);
        canvas.standardActivities[idx] = {
          ...canvas.standardActivities[idx],
          design: this.actionFormData.design || null,
        };
      }else {
        if (!canvas.activities) canvas.activities = [];

        if (this.isEditingAction && this.editingActionId) {
          // UPDATE existing activity
          const idx = canvas.activities.findIndex(a => a.id === this.editingActionId);
          if (idx !== -1) {
            canvas.activities[idx] = {
              ...canvas.activities[idx],
              name: this.actionFormData.name,
              design: this.actionFormData.design || null,
            };
          }
        } else {
          // CREATE new activity
          const newActivity = {
            id: this.actionFormData.id || commons.newUUID(),
            name: this.actionFormData.name,
            design: this.actionFormData.design || null,
            // master plan (2026-08-01): new forms start on the server-native formula engine
            serverNative: true,
          };
          canvas.activities.push(newActivity);
        }
      }

      this.$store.dispatch("updateVectorCanvas", canvas);

      await this.$store.dispatch("saveVector");
      console.debug('WorkflowDrawer:action:Saving view changes and close detail view.');
      window.eventBus.$emit("listing:schemaUpdated");

    },
    backToList() { 
      this.showDetailView = false;
      this.detailViewType = null;
    },
    getState(stateId) {
      if (this.states && stateId) {
        return this.states.find(s => s.id === stateId) || {};
      }
      return {};
    },
    getAction(actionId) {
      if (this.actions && actionId) {
        return this.actions.find(a => a.id === actionId) || null
      }
      return null;
    },
    getStateName(stateId) {
      const state = this.getState(stateId);
      return state.name || stateId;
    },
    getActionName(actionId) {
      const action = this.getAction(actionId);
      return action.name || actionId;
    },
    isStateUsedInFlows(stateId) {
      return this.flows.some(flow =>
        flow.fromStateId === stateId ||
        flow.toStateId === stateId ||
        (flow.conditionalStates && flow.conditionalStates.some(cs => cs.toStateId === stateId))
      );
    },
    isActionUsedInFlows(actionId) {
      return this.flows.some(flow => flow.activityId === actionId);
    },
    ensureFirstStateIsInitial(canvas) {
      if (canvas.states && canvas.states.length > 0) {
        canvas.states.forEach((state, index) => {
          state.initial = index === 0;
        });
      }
    },
    addStateColumnToListings(canvas) {
      // State column definition (from queryLogic.querySelectionPrefix)
      const stateColumn = {
        id: 'state',
        displayName: 'State',
        fieldName: 'state',
        type: 'state',
        system: true
      };

      // Check if canvas has menus (listings)
      if (canvas.menus && canvas.menus.length > 0) {
        canvas.menus.forEach(menu => {
          // Skip "everything" listing - it should not be affected by state changes
          if (menu.id === 'everything') {
            return;
          }
          // Only process listings that have a list.columns array
          if (menu.list && menu.list.columns) {
            // Check if state column already exists
            const hasStateColumn = menu.list.columns.some(col => col.id === 'state');
            if (!hasStateColumn) {

              if(this.insertAt){
                menu.list.columns.splice(this.insertAt, 0, stateColumn);
                return;
              }

              // Add state column before the action column (if exists)
              const actionIndex = menu.list.columns.findIndex(col => col.id === 'action');
              if (actionIndex > -1) {
                // Insert before action column
                menu.list.columns.splice(actionIndex, 0, stateColumn);
              } else {
                // Add at end if no action column
                menu.list.columns.push(stateColumn);
              }
            }
          }
        });
      }
    },
    removeStateColumnFromListings(canvas) {
      // Check if canvas has menus (listings)
      if (canvas.menus && canvas.menus.length > 0) {
        canvas.menus.forEach(menu => {
          // Skip "everything" listing - it should always keep state column
          if (menu.id === 'everything') {
            return;
          }
          // Only process listings that have a list.columns array
          if (menu.list && menu.list.columns) {
            // Find and remove state column
            const stateIndex = menu.list.columns.findIndex(col => col.id === 'state');
            if (stateIndex > -1) {
              menu.list.columns.splice(stateIndex, 1);
            }
          }
        });
      }
    },
    debounceAutoColorInput: _.debounce(async function () {
      if (this.stateFormData.name && this.stateFormData.name.trim()) {
        try {
          const res = await this.$store.dispatch("ai", {
            type: "state",
            text: this.stateFormData.name.trim()
          });
          if (!this.stateFormData.id) return;
          this.stateFormData.color = res.data;
          console.log("Autofill edit color:", res.data);
        } catch (err) {
          console.error("AI color generation error:", err);
        } finally {
          this.editAutofillLoading = false;
        }
      }
    }, 800),
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleStateClickOutside);
  },

  watch: {
    'stateFormData.id'(newId) {
      if (newId) {
        this.$nextTick(() => {
          document.addEventListener('mousedown', this.handleStateClickOutside);
        });
      } else {
        document.removeEventListener('mousedown', this.handleStateClickOutside);
      }
    },

    'stateFormData.name'(newValue) {
      
      if(this.originalFormData && this.originalFormData.name === newValue){
        return;
      }

      if (newValue && newValue.trim()) {
        
        if (!this.editAutofillLoading) this.editAutofillLoading = true;
        this.debounceAutoColorInput();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars.scss" as *;
@use "./src/assets/scss/colors" as *;

.fts-design-workflow-drawer {
  .fts-drawer-main {
    z-index: $z-index-modal;
  }
}

.workflow-drawer-header {
  padding-top: 1rem;

  hr {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--fts-border);
  }
}

.workflow-drawer-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
}

.workflow-drawer-header-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--fts-text);
}

.workflow-drawer-tabs {
  display: flex;
  gap: 0.5rem;
}

.workflow-drawer-tab {
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--fts-text-secondary);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    color: var(--fts-text);
  }

  &.active {
    color: var(--fts-primary);
    border-bottom-color: var(--fts-primary);
  }
}

.workflow-drawer-body {
  height: 100%;
  width: 100%;
  overflow: auto;
}

.workflow-drawer-tab-content {
  padding: 1.5rem;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.workflow-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  padding: 3rem 0;
  margin: 0 auto;
  text-align: center;
  flex: 1;

  &__icon {
    max-width: 100%;
    width: 160px;
    margin-bottom: 12px;
    > img {
      display: flex;
      align-items: center;
      justify-content: center;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      margin: auto;
    }
  }

  &__title {
    margin-bottom: 6px;
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-paragraph-text-16--line-height);
    color: var(--inistate-text-primary);
  }

  &__description {
    margin-bottom: 16px;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    color: var(--inistate-text-secondary);
  }
}

.workflow-dummy-block {
  height: 90px;
}

.workflow-item.default-action {
  background-color: rgba(var(--fts-primary-rgb), 0.15);

  &:hover {
    background-color: rgba(var(--fts-primary-rgb), 0.10);
  }
}

.workflow-item.editing {
  &:hover {
    padding: 0.6rem 1rem 0.6rem 1rem;
  }
}

.workflow-item {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  padding: 0.6rem 1rem 0.6rem 1rem;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: rgba(var(--fts-gray-100-rgb), 0.75);
  border-radius: var(--inistate-rounded-4);
  
  &:hover {
    background-color: rgba(var(--fts-gray-100-rgb), 0.75);
    padding: 0.6rem 1rem 0.6rem 2.2rem;
    

    .workflow-item__handle,
    .workflow-item__actions {
      visibility: visible !important;
    }
  }

  &__label {
    font-size: 0.70rem;
    padding: 4px;
    background-color: rgba(var(--fts-primary-rgb), 0.20);
    color: var(--fts-primary);
    border-radius: 2px;
    font-weight: 500;
  }

  &__handle {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--fts-default);
    font-size: 0.75rem;
    visibility: hidden;
    cursor: grab;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: auto;
    visibility: hidden;
  }

  &-content {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  &-state {
    flex: 1;
  }

  &-emoji {
    font-size: 1.25rem;
  }

  &-name {
    color: var(--fts-text);
    flex: 1;
  }

  &-description {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: var(--fts-text-secondary);
    line-height: 1.4;
  }

  &-color {
    margin-top: 0.5rem;
  }

  &-color-badge {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    border: 2px solid var(--fts-border);
  }
}

.workflow-flow-item {
  cursor: pointer;

  &:hover {
    .workflow-item__handle {
      visibility: visible !important;
    }
  }
}

.ghost {
  opacity: 0.5;
  background: var(--fts-background-secondary);
}

.workflow-flow-path {
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: -0.175rem;

  .flow-state,
  .flow-activity,
  .flow-no-activity,
  .flow-direction,
  .flow-conditional {
    margin: 0.175rem;
  }

  .flow-activity,
  .flow-no-activity,
  .flow-conditional {
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .flow-activity {
    min-width: 0;
    display: flex;
    align-items: center;

    .flow-activity-emoji {
      margin-right: 0.375rem;
    }

    .flow-activity-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: inherit;
      color: var(--fts-text);
    }
  }

  .flow-no-activity,
  .flow-direction,
  .flow-conditional {
    color: var(--fts-default);
  }
}

.workflow-flow-node {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.workflow-flow-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--fts-text-secondary);
  text-transform: uppercase;
}

.workflow-flow-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--fts-text);
}

.workflow-flow-arrow {
  color: var(--fts-text-secondary);
  font-size: 0.875rem;
  flex-shrink: 0;
}

.workflow-drawer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--fts-border);
  background-color: var(--fts-background);
}

.workflow-detail-view {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.workflow-detail-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--fts-border);
  background-color: var(--fts-background);
}

.workflow-detail-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--fts-text);
}

.workflow-detail-content {
  flex: 1;
  padding: 1.5rem;
  overflow: auto;
}

.workflow-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  hr {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--fts-border);
  }
}

.workflow-form-error {
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  color: var(--fts-danger);
  background-color: rgba(var(--fts-danger-rgb), 0.1);
  border-radius: 0.25rem;
  border-left: 3px solid var(--fts-danger);
}

.workflow-form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.workflow-form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--fts-text);
}

.workflow-form-hint {
  font-size: 0.75rem;
  color: var(--fts-text-secondary);
  line-height: 1.4;
}

.workflow-form-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.workflow-form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
}

.workflow-inline-input {
  flex: 1;
  padding: 0.5rem;
  font-size: 0.875rem;
  color: var(--fts-text);
  background-color: var(--fts-background);
  border: 1px solid var(--fts-border);
  border-radius: 0.25rem;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--fts-primary);
  }

  &::placeholder {
    color: var(--fts-text-secondary);
  }
}

// Footer
.info-drawer-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--fts-border);
}

.info-drawer-actions-spacer {
  flex: 1;
}
</style>

<style lang="scss">
  .fts-design-workflow-drawer {
    .tab-header {
      align-items: center;
      display: flex;
      gap: 4px;
      margin-bottom: 12px;
    }

    .tab-title {
      font-weight: var(--inistate-font-weight-medium);
      font-size: var(--inistate-paragraph-text-16);
      line-height: var(--inistate-paragraph-text-16--line-height);
      color: var(--inistate-text-secondary);
    }

    .tab-hint {
      height: 20px;
      width: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .tab-hint-icon {
      font-size: 18px;
      color: var(--inistate-icon-primary);
    }
  }
</style>